import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { feedbackFormsAPI, emailAPI, cyclesAPI } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Search, CheckCircle, Clock, Eye, Link2, ExternalLink, Archive, Mail, Bell, ChevronLeft, FileBarChart2, FolderDown } from 'lucide-react';
import { toast } from 'sonner';
import CyclePickerGrid from '../components/CyclePickerGrid';
import { usePagination, PaginationBar } from '../components/PaginationBar';
import FeedbackReportDialog, { downloadBlobResponse } from '../components/FeedbackReportDialog';
import api from '../lib/api';

const AllFeedback = ({ scopedCycleId, hideLayout = false }) => {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [reportPlacementId, setReportPlacementId] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchParams, setSearchParams] = useSearchParams();
  // When embedded inside a cycle detail we always operate on that cycle and
  // hide the cycle picker + breadcrumb. Otherwise fall back to the URL param.
  const cycleParam = scopedCycleId || (searchParams.get('cycleId') || '');
  const embedded = Boolean(scopedCycleId);

  useEffect(() => {
    Promise.all([
      feedbackFormsAPI.getAll({}),
      cyclesAPI.list(),
    ])
      .then(([f, c]) => { setForms(f.data || []); setCycles(c.data || []); })
      .catch(() => toast.error('Failed to load feedback forms'))
      .finally(() => setLoading(false));
  }, []);

  const handleSendEmail = async (formId) => {
    try {
      const res = await emailAPI.sendAssessment({ form_ids: [formId], base_url: window.location.origin });
      if (res.data.sent > 0) toast.success('Assessment email sent');
      else toast.error(res.data.errors?.[0]?.error || 'Failed to send');
    } catch { toast.error('Failed to send email'); }
  };

  const handleSendReminder = async (formId) => {
    try {
      const res = await emailAPI.sendReminder({ form_ids: [formId], base_url: window.location.origin });
      if (res.data.sent > 0) toast.success('Reminder sent');
      else toast.error(res.data.errors?.[0]?.error || 'Failed to send');
    } catch { toast.error('Failed to send reminder'); }
  };

  const handleBulkEmail = async () => {
    const pending = filtered.filter(f => f.status === 'pending' && !f.archived);
    if (pending.length === 0) { toast.info('No pending forms to email'); return; }
    try {
      const res = await emailAPI.sendAssessment({ form_ids: pending.map(f => f.id), base_url: window.location.origin });
      toast.success(`Sent ${res.data.sent} emails${res.data.errors?.length ? `, ${res.data.errors.length} failed` : ''}`);
    } catch { toast.error('Failed to send emails'); }
  };

  const copyLink = (accessToken) => {
    const url = `${window.location.origin}/feedback/${accessToken}`;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url).then(
        () => toast.success('Link copied'),
        () => fallbackCopy(url)
      );
    } else {
      fallbackCopy(url);
    }
  };

  const fallbackCopy = (url) => {
    const textarea = document.createElement('textarea');
    textarea.value = url;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try { document.execCommand('copy'); toast.success('Link copied'); }
    catch { toast.info(`Link: ${url}`, { duration: 10000 }); }
    document.body.removeChild(textarea);
  };

  // Scope forms to the currently drilled-in cycle (or "unassigned" bucket
  // for legacy forms whose placement isn't linked to any cycle).
  const cycleScopedForms = useMemo(() => {
    if (!cycleParam) return forms;
    if (cycleParam === 'unassigned') return forms.filter((f) => !f.cycle_id);
    return forms.filter((f) => f.cycle_id === cycleParam);
  }, [forms, cycleParam]);

  const unassignedCount = useMemo(
    () => forms.filter((f) => !f.cycle_id).length,
    [forms],
  );

  const selectedCycle = useMemo(
    () => (cycleParam && cycleParam !== 'unassigned' ? cycles.find((c) => c.id === cycleParam) : null),
    [cycles, cycleParam],
  );

  // Feed CyclePickerGrid a lightweight "placement" shape derived from forms
  // so the grid can compute per-cycle progress bars using the same helpers
  // as Placements / Documents.
  const cyclePlacementsForGrid = useMemo(
    () => {
      const seen = new Set();
      const out = [];
      forms.forEach((f) => {
        if (!f.placement_id || seen.has(f.placement_id)) return;
        seen.add(f.placement_id);
        out.push({
          id: f.placement_id,
          cycle_id: f.cycle_id || null,
          restaurant_id: f.restaurant_id,
          student_id: f.student_id,
          status: 'active',
        });
      });
      return out;
    },
    [forms],
  );

  const filtered = cycleScopedForms.filter((f) => {
    const matchesSearch =
      f.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.restaurant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.chef_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'archived' ? f.archived : f.status === statusFilter && !f.archived);
    return matchesSearch && matchesStatus;
  });

  // Group by student year (derived from forms — need to fetch student year)
  // Since feedback forms don't store student_year, we'll group by student name
  // But we need year — let's sort by student name A-Z
  const sorted = [...filtered].sort((a, b) => a.student_name.localeCompare(b.student_name));
  const pg = usePagination(sorted, 'feedback');

  // Group by student (within the current page)
  const grouped = pg.pageItems.reduce((acc, form) => {
    const key = form.student_name;
    if (!acc[key]) acc[key] = [];
    acc[key].push(form);
    return acc;
  }, {});

  if (loading) {
    const loader = (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
      </div>
    );
    return hideLayout || embedded ? loader : <Layout>{loader}</Layout>;
  }

  const downloadCycleZip = async () => {
    if (!cycleParam || cycleParam === 'unassigned') return;
    setZipping(true);
    try {
      const res = await api.get(`/feedback-reports/cycle/${cycleParam}/zip`, { responseType: 'blob' });
      downloadBlobResponse(res, 'Feedback_Reports.zip');
    } catch (e) {
      const msg = e.response?.data instanceof Blob ? await e.response.data.text().then((t) => { try { return JSON.parse(t).detail; } catch { return null; } }) : e.response?.data?.detail;
      toast.error(msg || 'ZIP download failed');
    } finally {
      setZipping(false);
    }
  };
  const hasSubmitted = cycleScopedForms.some((f) => f.status === 'submitted');

  const body = (
    <div data-testid="all-feedback-page" className="space-y-6 animate-fade-in">
      <FeedbackReportDialog placementId={reportPlacementId} open={!!reportPlacementId} onOpenChange={(o) => { if (!o) setReportPlacementId(null); }} />
      {cycleParam && cycleParam !== 'unassigned' && hasSubmitted && (
        <div className="flex justify-end -mb-2">
          <Button data-testid="feedback-cycle-zip-btn" size="sm" variant="outline" onClick={downloadCycleZip} disabled={zipping}>
            <FolderDown className="w-4 h-4 mr-2" /> {zipping ? 'Preparing ZIP…' : 'Download all consolidated reports (ZIP)'}
          </Button>
        </div>
      )}
      {!embedded && (
        <div>
          {cycleParam ? (
            <>
              <button
                type="button"
                data-testid="feedback-back-to-cycles"
                onClick={() => setSearchParams({}, { replace: true })}
                className="inline-flex items-center gap-1 text-xs font-medium text-ica-navy hover:underline mb-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back to cycles
              </button>
              <h1 className="font-heading text-3xl font-bold text-ica-navy" data-testid="feedback-breadcrumb">
                <span className="text-gray-400 font-normal">Placement Feedback ›</span>{' '}
                {cycleParam === 'unassigned' ? 'Unassigned' : (selectedCycle?.name || 'Cycle')}
              </h1>
              <p className="text-gray-500 mt-1">View and manage assessment forms for this cycle.</p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-bold text-ica-navy">Placement Feedback</h1>
              <p className="text-gray-500 mt-1">Pick a placement cycle to review its assessment forms.</p>
            </>
          )}
        </div>
      )}

      {(!embedded && !cycleParam) ? (
        <CyclePickerGrid
          cycles={cycles}
          placements={cyclePlacementsForGrid}
          unassignedCount={unassignedCount}
          showAssessments
          onSelect={(cid) => {
            const next = new URLSearchParams(searchParams);
            next.set('cycleId', cid == null ? 'unassigned' : cid);
            setSearchParams(next, { replace: false });
          }}
          testIdPrefix="feedback-cycle"
        />
      ) : (
      <>
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                data-testid="search-feedback"
                placeholder="Search by student, restaurant, or chef..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="feedback-status-filter" className="w-40">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {filtered.some(f => f.status === 'pending' && !f.archived) && (
            <Button
              data-testid="bulk-email-all-btn"
              variant="outline"
              onClick={handleBulkEmail}
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email All Pending
            </Button>
          )}
        </div>

        {/* Summary */}
        <div className="flex gap-4">
          <div className="px-4 py-2 bg-white rounded-lg border text-sm">
            <span className="text-gray-500">Total:</span>{' '}
            <span className="font-semibold text-ica-navy">{filtered.length}</span>
          </div>
          <div className="px-4 py-2 bg-white rounded-lg border text-sm">
            <span className="text-gray-500">Completed:</span>{' '}
            <span className="font-semibold text-emerald-600">{filtered.filter(f => f.status === 'submitted').length}</span>
          </div>
          <div className="px-4 py-2 bg-white rounded-lg border text-sm">
            <span className="text-gray-500">Pending:</span>{' '}
            <span className="font-semibold text-amber-600">{filtered.filter(f => f.status === 'pending').length}</span>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-gray-500">No feedback forms found.</p>
              </div>
            ) : (
              <>
              <PaginationBar {...pg.bar} position="top" noun="forms" />
              <div className="table-scroll">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Student</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Restaurant</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Assessment</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Template</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Due Date</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Chef</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(grouped).map(([studentName, studentForms]) => (
                      <React.Fragment key={studentName}>
                        <tr className="bg-ica-navy/5">
                          <td colSpan={8} className="py-2 px-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-sm text-ica-navy">{studentName}</span>
                              <span className="text-xs text-gray-500" data-testid={`feedback-progress-${studentForms[0].placement_id}`}>
                                {studentForms.filter(f => f.status === 'submitted').length}/{studentForms.length} assessments submitted
                              </span>
                              {studentForms.some(f => f.status === 'submitted') && (
                                <button
                                  type="button"
                                  data-testid={`feedback-report-btn-${studentForms[0].placement_id}`}
                                  onClick={() => setReportPlacementId(studentForms[0].placement_id)}
                                  className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-ica-navy border border-ica-navy/30 rounded px-2 py-0.5 hover:bg-ica-navy hover:text-white transition-colors"
                                >
                                  <FileBarChart2 className="w-3.5 h-3.5" /> Consolidated report
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {studentForms
                          .sort((a, b) => a.assessment_number - b.assessment_number)
                          .map((form) => (
                          <tr key={form.id} data-testid={`feedback-row-${form.id}`} className="border-b hover:bg-ica-gold/5 transition-colors">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-ica-navy/10 rounded-full flex items-center justify-center text-ica-navy font-semibold text-xs">
                                  {form.student_name.charAt(0)}
                                </div>
                                <span className="text-sm font-medium text-gray-900">{form.student_name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-700">{form.restaurant_name}</td>
                            <td className="py-3 px-4">
                              <span className="text-sm font-medium text-gray-800">#{form.assessment_number}</span>
                              {form.phase_name && (
                                <span data-testid={`feedback-phase-${form.id}`} className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-ica-navy/10 text-ica-navy">{form.phase_name}</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500">{form.template_name}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{formatDate(form.due_date)}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{form.chef_name || '-'}</td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                form.archived
                                  ? 'bg-gray-100 text-gray-600'
                                  : form.status === 'submitted'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {form.archived ? <Archive className="w-3 h-3" /> : form.status === 'submitted' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                {form.archived ? 'Archived' : form.status === 'submitted' ? 'Submitted' : 'Pending'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-1">
                                {form.status === 'submitted' ? (
                                  <button
                                    data-testid={`view-feedback-${form.id}`}
                                    onClick={() => navigate(`/feedback-view/${form.id}`)}
                                    className="p-1.5 hover:bg-blue-50 rounded text-blue-600 transition-colors"
                                    title="View submission"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      data-testid={`email-feedback-${form.id}`}
                                      onClick={() => handleSendEmail(form.id)}
                                      className="p-1.5 hover:bg-blue-50 rounded text-blue-600 transition-colors"
                                      title="Email assessment link to chef"
                                    >
                                      <Mail className="w-4 h-4" />
                                    </button>
                                    <button
                                      data-testid={`remind-feedback-${form.id}`}
                                      onClick={() => handleSendReminder(form.id)}
                                      className="p-1.5 hover:bg-amber-50 rounded text-amber-600 transition-colors"
                                      title="Send reminder"
                                    >
                                      <Bell className="w-4 h-4" />
                                    </button>
                                    <button
                                      data-testid={`copy-link-feedback-${form.id}`}
                                      onClick={() => copyLink(form.access_token)}
                                      className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors"
                                      title="Copy chef link"
                                    >
                                      <Link2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar {...pg.bar} position="bottom" noun="forms" />
              </>
            )}
          </CardContent>
        </Card>
      </>
      )}
    </div>
  );

  return hideLayout || embedded ? body : <Layout>{body}</Layout>;
};

export default AllFeedback;
